import { isReactNativeWebView, postToNativeWebView } from '../../../native';

const NATIVE_DOWNLOAD_TIMEOUT_MS = 30_000;

type NativeDownloadResultPayload = {
  action?: string;
  requestId?: string;
  status?: string;
  error?: string;
};

const createDownloadRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file for native download'));
        return;
      }

      const [, base64 = ''] = result.split(',');
      resolve(base64);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read file for native download'));
    };
    reader.readAsDataURL(blob);
  });

const createNativeDownloadResultWaiter = (
  requestId: string
): {
  cancel: () => void;
  promise: Promise<void>;
} => {
  let handleMessage: ((event: MessageEvent) => void) | undefined;
  let timeoutId: ReturnType<typeof setTimeout>;

  const cleanup = () => {
    clearTimeout(timeoutId);
    if (handleMessage) {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as EventListener);
    }
  };

  const promise = new Promise<void>((resolve, reject) => {
    handleMessage = (event: MessageEvent) => {
      let data: NativeDownloadResultPayload;
      try {
        data = typeof event.data === 'string' ? (JSON.parse(event.data) as NativeDownloadResultPayload) : event.data;
      } catch {
        return;
      }

      if (data.action !== 'downloadFileResult' || data.requestId !== requestId) {
        return;
      }

      cleanup();
      if (data.status === 'success') {
        resolve();
        return;
      }

      reject(new Error(data.error || 'Native file download failed'));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Native file download timed out'));
    }, NATIVE_DOWNLOAD_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as EventListener);
  });

  const cancel = () => {
    cleanup();
  };

  return { cancel, promise };
};

const postNativeDownloadFile = async ({
  base64,
  fileName,
  mimeType,
  url,
}: {
  base64?: string;
  fileName: string;
  mimeType: string;
  url?: string;
}): Promise<void> => {
  const requestId = createDownloadRequestId();
  const result = createNativeDownloadResultWaiter(requestId);
  const didPost = postToNativeWebView('downloadFile', {
    base64,
    fileName,
    mimeType,
    requestId,
    url,
  });

  if (!didPost) {
    result.cancel();
    throw new Error('React Native WebView not available');
  }

  await result.promise;
};

const autoDownloadBlobInNativeWebView = async (blob: Blob, fileName: string): Promise<void> => {
  const base64 = await blobToBase64(blob);
  await postNativeDownloadFile({
    base64,
    fileName,
    mimeType: blob.type || 'application/octet-stream',
  });
};

export const autoDownloadUrlInNativeWebView = async (
  url: string,
  fileName: string,
  mimeType = 'application/octet-stream'
): Promise<void> => {
  if (!isReactNativeWebView()) {
    throw new Error('React Native WebView not available');
  }

  await postNativeDownloadFile({
    fileName,
    mimeType,
    url,
  });
};

export const autoDownloadBlob = async (blob: Blob, fileName: string) => {
  if (isReactNativeWebView()) {
    await autoDownloadBlobInNativeWebView(blob, fileName);
    return;
  }

  const link = URL.createObjectURL(blob);

  const anchorElement = document.createElement('a');

  anchorElement.href = link;

  anchorElement.setAttribute('download', fileName);

  anchorElement.setAttribute('target', '_blank');

  document.body.appendChild(anchorElement);

  anchorElement.click();

  anchorElement.remove();
  window.URL.revokeObjectURL(link);
};
